import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus } from "lucide-react";
import { normalizeProductUnit, PRODUCT_UNIT_MAX } from "@shared/productUnit";
import { SIZE_UNIT_SUGGESTIONS, sizeUnitSuffix } from "@shared/productSizes";
import { composeProductName, productParts } from "@shared/productNames";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { parseApiError } from "@/components/pin-setup-code-step";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { unitsOfItem } from "@/lib/itemUnits";

// What the section reads off a product — the Settings page's own product shape fits.
type UnitProduct = {
  id: number;
  name: string;
  itemName?: string | null;
  pack?: string | null;
  categoryId: number;
  isActive: boolean;
  unit?: string | null;
  price?: string | null;
  sizeOfProductId?: number | null;
  caseFraction?: string | null;
  unitOrder?: number | null;
};

/**
 * The item form → Units: every way this item is sold, one line per unit, and "Add a
 * unit". Each unit is its own product — own price, QuickBooks item and order lines —
 * named the item and the unit joined (shared/productNames.ts); a line is the same form
 * opened on another unit, so the item is always edited in one place. The unit the form
 * is open on is the highlighted line; its fields sit below the list. Dragging a line by
 * its handle sets the order the units are listed in everywhere — this form, the Master
 * Menu, the customer's card (products.unit_order, PUT /products/:id/unit-order).
 */
export function ProductUnitsSection<P extends UnitProduct>({
  product,
  products,
  item,
  onOpenUnit,
}: {
  product: P;
  products: P[];
  /** The item as typed in the form's Item box, for the name a new unit will print as. */
  item: string;
  /** Open the form on another unit of this item (the page asks about unsaved changes). */
  onOpenUnit: (product: P) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [unit, setUnit] = useState("");
  const [price, setPrice] = useState("");

  const listed = unitsOfItem(product, products);
  // The order just dragged, held until the saved list comes back so the line does not jump.
  const [dragged, setDragged] = useState<number[] | null>(null);
  const units = dragged
    ? dragged.map((id) => listed.find((member) => member.id === id)).filter((member): member is P => !!member)
    : listed;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const takenUnits = new Set(units.map((member) => member.unit));
  const inGroup = units.length > 1;
  const groupIds = new Set(products.map((p) => p.sizeOfProductId).filter((id): id is number => id != null));
  // Products that can join this item: in no item with other units yet. Same category first.
  const linkable = products
    .filter((p) => !units.some((member) => member.id === p.id) && p.sizeOfProductId == null && !groupIds.has(p.id))
    .sort((a, b) => Number(b.categoryId === product.categoryId) - Number(a.categoryId === product.categoryId) || a.name.localeCompare(b.name));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/products"] });
    queryClient.invalidateQueries({ queryKey: ["/api/products/active"] });
  };
  const failed = (title: string) => (error: unknown) =>
    toast({ title, description: parseApiError(error).message ?? "Please try again.", variant: "destructive" });

  const typedUnit = normalizeProductUnit(unit);
  const savedItem = productParts(product).item;

  const addUnit = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/products/${product.id}/sizes`, { unit, price: price.trim() || null });
      return (await res.json()) as { name: string };
    },
    onSuccess: (created) => {
      refresh();
      setAdding(false);
      setUnit("");
      setPrice("");
      toast({ title: `${created.name} added`, description: "Customers see it on this item's card." });
    },
    onError: failed("Couldn't add the unit"),
  });

  const setLead = useMutation({
    mutationFn: async ({ id, leadId }: { id: number; leadId: number | null }) => {
      await apiRequest("PUT", `/api/products/${id}/size-of`, { leadId });
    },
    onSuccess: refresh,
    onError: failed("Couldn't change the item's units"),
  });

  const saveOrder = useMutation({
    mutationFn: async (ids: number[]) => {
      await apiRequest("PUT", `/api/products/${product.id}/unit-order`, { ids });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products/active"] });
      setDragged(null);
    },
    onError: (error) => {
      setDragged(null);
      failed("Couldn't save the order")(error);
    },
  });

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = units.map((member) => member.id);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from === -1 || to === -1) return;
    const next = arrayMove(ids, from, to);
    setDragged(next);
    saveOrder.mutate(next);
  };

  // The lead's id: what an existing product joins.
  const leadId = product.sizeOfProductId ?? product.id;

  return (
    <div className="rounded-lg border border-gray-200 p-3" data-testid="product-units-section">
      <Label>Units</Label>
      <p className="mt-0.5 text-xs text-gray-500">
        Each way this item is sold, like a case or a single. Each has its own price and prints as its own line.
        {listed.length > 1 && " Drag a line by its handle to set the order customers see them in."}
      </p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={units.map((member) => member.id)} strategy={verticalListSortingStrategy}>
          <ul className="mt-2 space-y-1.5" data-testid="list-item-units">
            {units.map((member) => (
              <UnitLine
                key={member.id}
                member={member}
                current={member.id === product.id}
                canMove={units.length > 1 && !saveOrder.isPending}
                onOpen={() => onOpenUnit(member)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {!adding && (
        <Button type="button" variant="outline" size="sm" className="mt-2 h-8" onClick={() => setAdding(true)} data-testid="button-add-unit">
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add a unit
        </Button>
      )}

      {adding && (
        <div className="mt-3 space-y-2 rounded-md bg-gray-50 p-2.5">
          <div className="flex flex-wrap gap-1.5">
            {SIZE_UNIT_SUGGESTIONS.filter((suggested) => !takenUnits.has(suggested)).map((suggested) => (
              <button
                key={suggested}
                type="button"
                aria-pressed={typedUnit === suggested}
                onClick={() => setUnit(suggested)}
                data-testid={`chip-new-unit-${suggested}`}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  typedUnit === suggested
                    ? "border-cn-brand-green bg-green-50 font-semibold text-cn-brand-green"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {suggested}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              maxLength={PRODUCT_UNIT_MAX}
              autoComplete="off"
              placeholder="Unit, e.g. 2 lb bag"
              aria-label="New unit"
              className="bg-white"
              data-testid="input-new-unit"
            />
            <div className="relative w-28 flex-shrink-0">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
              <Input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputMode="decimal"
                placeholder="Price"
                aria-label="New unit's price"
                className="bg-white pl-7"
                data-testid="input-new-unit-price"
              />
            </div>
          </div>
          {typedUnit && (
            <p className="text-xs text-gray-500" data-testid="text-new-unit-name">
              Prints as{" "}
              <span className="font-medium text-gray-700">{composeProductName({ item: savedItem, unit: typedUnit })}</span>
              {item.trim() && item.trim() !== savedItem && " (until you save the new item name)"}
            </p>
          )}
          {typedUnit && takenUnits.has(typedUnit) && (
            <p className="text-xs text-red-600">This item is already sold by the {typedUnit}.</p>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => addUnit.mutate()}
              disabled={!typedUnit || takenUnits.has(typedUnit) || addUnit.isPending}
              className="bg-cn-brand-green hover:bg-cn-brand-green-hover"
              data-testid="button-save-unit"
            >
              {addUnit.isPending ? "Adding…" : "Add unit"}
            </Button>
          </div>
        </div>
      )}

      {!adding && (linkable.length > 0 || inGroup) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2.5">
          {linkable.length > 0 && (
            <Select value="" onValueChange={(value) => setLead.mutate({ id: Number(value), leadId })}>
              <SelectTrigger className="h-8 w-auto min-w-[11rem] text-xs" data-testid="select-link-unit">
                <SelectValue placeholder="Add an existing product as a unit" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {linkable.map((candidate) => (
                  <SelectItem key={candidate.id} value={String(candidate.id)}>
                    {candidate.name}
                    {candidate.unit ? ` (${candidate.unit})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {inGroup && (
            <button
              type="button"
              className="text-xs text-gray-500 underline hover:text-red-600"
              onClick={() => setLead.mutate({ id: product.id, leadId: null })}
              disabled={setLead.isPending}
              data-testid="button-split-unit"
            >
              {setLead.isPending ? "Moving…" : `Make the ${product.unit ?? "open"} unit its own item`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// One unit of the item: its handle, and the unit with its price — a button that opens
// the form on it. The unit the form is open on is the highlighted line.
function UnitLine({
  member,
  current,
  canMove,
  onOpen,
}: {
  member: UnitProduct;
  current: boolean;
  canMove: boolean;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
    disabled: !canMove,
  });
  const { pack } = productParts(member);
  const label = member.unit ? sizeUnitSuffix(member.unit) : "no unit";
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1 rounded-md border pr-1 ${
        current ? "border-cn-brand-green bg-green-50" : "border-gray-200 bg-white"
      } ${isDragging ? "relative z-10 shadow-md" : ""}`}
      data-testid={`row-item-unit-${member.id}`}
    >
      <button
        type="button"
        aria-label={`Move the ${label} unit`}
        disabled={!canMove}
        className="touch-none cursor-grab px-2 py-2.5 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-30"
        data-testid={`handle-item-unit-${member.id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-current={current ? "true" : undefined}
        onClick={() => !current && onOpen()}
        data-testid={`line-item-unit-${member.id}`}
        className={`flex min-w-0 flex-1 items-center justify-between gap-3 rounded py-2 pr-1 text-left text-sm ${
          current ? "cursor-default font-semibold text-cn-brand-green" : "text-gray-700 hover:text-gray-900"
        }`}
      >
        <span className="min-w-0 truncate">
          {label}
          {pack && <span className="ml-1.5 font-normal text-gray-500">{pack}</span>}
          {!member.isActive && <span className="ml-1.5 text-xs font-normal text-gray-400">off</span>}
        </span>
        <span className={`flex-shrink-0 tabular-nums ${current ? "font-normal" : "text-gray-500"}`}>
          {member.price ? `$${member.price}` : "no price"}
        </span>
      </button>
    </li>
  );
}
